import { createContext, useCallback, useMemo, useState } from "react";

export type ChannelInfo = { id: string; needsReply: boolean };

type SelectedChannelsContextValue = {
  selectedChannels: string[];
  setSelectedChannels: React.Dispatch<React.SetStateAction<string[]>>;
  availableChannels: string[];
  channelNeedsReply: Record<string, boolean>;
  setChannelsForSource: (source: string, channels: ChannelInfo[]) => void;
};

export const SelectedChannelsContext =
  createContext<SelectedChannelsContextValue>({
    selectedChannels: [],
    setSelectedChannels: () => {},
    availableChannels: [],
    channelNeedsReply: {},
    setChannelsForSource: () => {},
  });

export const SelectedChannels = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [channelsBySource, setChannelsBySource] = useState<
    Record<string, ChannelInfo[]>
  >({});

  const setChannelsForSource = useCallback(
    (source: string, channels: ChannelInfo[]) => {
      setChannelsBySource((prev) => ({ ...prev, [source]: channels }));
    },
    []
  );

  const { availableChannels, channelNeedsReply } = useMemo(() => {
    const seen = new Set<string>();
    const ids: string[] = [];
    const replyMap: Record<string, boolean> = {};
    Object.values(channelsBySource).forEach((channels) => {
      channels.forEach(({ id, needsReply }) => {
        if (!seen.has(id)) {
          seen.add(id);
          ids.push(id);
        }
        replyMap[id] = needsReply;
      });
    });
    return { availableChannels: ids, channelNeedsReply: replyMap };
  }, [channelsBySource]);

  return (
    <SelectedChannelsContext.Provider
      value={{
        selectedChannels,
        setSelectedChannels,
        availableChannels,
        channelNeedsReply,
        setChannelsForSource,
      }}
    >
      {children}
    </SelectedChannelsContext.Provider>
  );
};
